import { certsFormattedMessage, getMessageKey } from '../../../utils/utils';
import { CERT_EO_CAMPAIGN_ROW_TYPE } from '../../CertsDetailsList/Tile/constant';
import {
  ACTION_BUTTON_KINDS,
  ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES,
} from '../constant';

export const trackActionSelect = (prev, actionKind, status, trackerId) =>
  (Array.isArray(prev) ? prev : []).map((item) => {
    if (!item || !item.tracker) return item;

    const actions = item.tracker?.actions || {};
    const currentAction = actions[actionKind] || {};

    return {
      ...item,
      tracker: item.tracker
        ? {
            ...item.tracker,
            actions: {
              ...actions,
              [actionKind]: {
                isLoading: false,
                isSelected:
                  item.tracker?.id === trackerId
                    ? status
                    : currentAction.isSelected,
              },
            },
          }
        : item.tracker,
      children:
        item.children?.length > 0
          ? trackActionSelect(item.children, actionKind, status, trackerId)
          : [],
    };
  });

export const trackActionLoading = (prev, actionKind, status, trackerId) =>
  (Array.isArray(prev) ? prev : []).map((item) => {
    if (!item || !item.tracker) return item;

    const actions = item.tracker?.actions || {};
    const currentAction = actions[actionKind] || {};

    return {
      ...item,
      tracker: item.tracker
        ? {
            ...item.tracker,
            actions: {
              ...actions,
              [actionKind]: {
                ...currentAction,
                isLoading:
                  item.tracker?.id === trackerId
                    ? status
                    : currentAction.isLoading,
              },
            },
          }
        : item.tracker,
      children:
        item.children?.length > 0
          ? trackActionLoading(item.children, actionKind, status, trackerId)
          : [],
    };
  });

export const setChildrenApprovalFalse = (nodeData, setMergedLists) => {
  const setApprovalFalse = (node) => {
    // Check if the current node matches the tracker id
    if (node.tracker && node.tracker.id === nodeData.tracker.id) {
      // Apply changes to all descendants
      const updateDescendants = (currentNode) => ({
        ...currentNode,
        tracker:
          currentNode.tracker && currentNode.tracker.actions
            ? {
                ...currentNode.tracker,
                actions: {
                  ...currentNode.tracker.actions,
                  approve: {
                    ...currentNode.tracker.actions.approve,
                    isSelected: false,
                  },
                },
              }
            : currentNode.tracker,
        children: currentNode.children
          ? currentNode.children.map(updateDescendants)
          : [],
      });

      // Start updating from the children
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: node.children.map(updateDescendants),
        };
      }
    } else if (node.children && node.children.length > 0) {
      // If not a match, continue to recursively check its children
      return {
        ...node,
        children: node.children.map(setApprovalFalse),
      };
    }

    // Return the node unchanged if no conditions are met
    return node;
  };

  // Update the list using the function
  setMergedLists((prev) => prev.map((rootNode) => setApprovalFalse(rootNode)));
};

export const trackActionDisable = (prev, actionKind, status, trackerId) =>
  (Array.isArray(prev) ? prev : []).map((item) => {
    if (!item || !item.tracker) return item;

    const actions = item.tracker?.actions || {};
    const currentAction = actions[actionKind] || {};

    return {
      ...item,
      tracker: {
        ...item.tracker,
        actions: {
          ...actions,
          [actionKind]: {
            ...currentAction,
            isDisabled:
              item.tracker.id === trackerId ? status : currentAction.isDisabled,
          },
        },
      },
      children:
        item.children?.length > 0
          ? trackActionLoading(item.children, actionKind, status, trackerId)
          : [],
    };
  });

export const getToolTipForConsult = (nodeData, messages, intl, localize) => {
  let toolTip =
    messages.CONSULT_WITH_PEERS ||
    localize(intl, getMessageKey('CONSULT_WITH_PEERS'));

  if (nodeData?.isConsulted) {
    if (nodeData?.CONSULTED_USERCOUNT === 1) {
      toolTip = certsFormattedMessage(
        messages.CERTS_CONSULT_USERNAME_TEXT ||
          localize(intl, getMessageKey('CERTS_CONSULT_USERNAME_TEXT')),
        {
          userinfo: nodeData?.CONSULTED_USERNAME,
        }
      );
    } else if (nodeData?.CONSULTED_USERCOUNT > 1) {
      toolTip = certsFormattedMessage(
        messages.CERTS_CONSULT_MULTIPLE_USERS_TEXT ||
          localize(intl, getMessageKey('CERTS_CONSULT_MULTIPLE_USERS_TEXT')),
        {
          count: nodeData?.CONSULTED_USERCOUNT || 1,
        }
      );
    }
  }

  return toolTip;
};

export const getCommentRequiredForEOCampaign = (
  buttonConfig,
  rowType,
  entitlements,
  accounts,
  childEntilements
) => {
  if (rowType?.toString() === CERT_EO_CAMPAIGN_ROW_TYPE.ENTITLEMENT) {
    // Entitlements
    if (ACTION_BUTTON_KINDS.WORKS_FOR_ME === buttonConfig.id) {
      return entitlements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
      );
    }

    if (ACTION_BUTTON_KINDS.DECOMMISSION === buttonConfig.id) {
      return entitlements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
      );
    }

    if (ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME === buttonConfig.id) {
      return entitlements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.DOES_NOT_BELONG_TO_ME
      );
    }

    if (ACTION_BUTTON_KINDS.CONSULT === buttonConfig.id) {
      return entitlements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.CONSULT
      );
    }
  }

  if (rowType?.toString() === CERT_EO_CAMPAIGN_ROW_TYPE.ACCOUNT) {
    // Accounts
    if (ACTION_BUTTON_KINDS.APPROVE === buttonConfig.id) {
      return accounts.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
      );
    }

    if (ACTION_BUTTON_KINDS.REJECT === buttonConfig.id) {
      return accounts.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
      );
    }

    if (ACTION_BUTTON_KINDS.REASSIGN === buttonConfig.id) {
      return accounts.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REASSIGNSTEP2
      );
    }

    if (ACTION_BUTTON_KINDS.CONSULT === buttonConfig.id) {
      return accounts.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.CONSULT
      );
    }
  }

  if (rowType?.toString() === CERT_EO_CAMPAIGN_ROW_TYPE.CHILD_ENTITLEMENT) {
    // Child entitlements
    if (ACTION_BUTTON_KINDS.APPROVE === buttonConfig.id) {
      return childEntilements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
      );
    }

    if (ACTION_BUTTON_KINDS.REJECT === buttonConfig.id) {
      return childEntilements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
      );
    }

    if (ACTION_BUTTON_KINDS.REASSIGN === buttonConfig.id) {
      return childEntilements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REASSIGNSTEP2
      );
    }
  }

  return false;
};

export const getCommentRequiredForROCampaign = (
  buttonConfig,
  rowType,
  entitlements,
  accounts,
  childEntilements
) => {
  if (rowType?.toString() === CERT_EO_CAMPAIGN_ROW_TYPE.ENTITLEMENT) {
    // Entitlements
    if (ACTION_BUTTON_KINDS.WORKS_FOR_ME === buttonConfig.id) {
      return entitlements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
      );
    }

    if (ACTION_BUTTON_KINDS.DECOMMISSION === buttonConfig.id) {
      return entitlements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
      );
    }

    if (ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME === buttonConfig.id) {
      return entitlements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.DOES_NOT_BELONG_TO_ME
      );
    }

    if (ACTION_BUTTON_KINDS.CONSULT === buttonConfig.id) {
      return entitlements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.CONSULT
      );
    }
  }

  if (rowType?.toString() === CERT_EO_CAMPAIGN_ROW_TYPE.ACCOUNT) {
    // Accounts
    if (ACTION_BUTTON_KINDS.APPROVE === buttonConfig.id) {
      return accounts.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
      );
    }

    if (ACTION_BUTTON_KINDS.REJECT === buttonConfig.id) {
      return accounts.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
      );
    }

    if (ACTION_BUTTON_KINDS.REASSIGN === buttonConfig.id) {
      return accounts.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REASSIGNSTEP2
      );
    }

    if (ACTION_BUTTON_KINDS.CONSULT === buttonConfig.id) {
      return accounts.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.CONSULT
      );
    }
  }

  if (rowType?.toString() === CERT_EO_CAMPAIGN_ROW_TYPE.CHILD_ENTITLEMENT) {
    // Child entitlements
    if (ACTION_BUTTON_KINDS.APPROVE === buttonConfig.id) {
      return childEntilements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
      );
    }

    if (ACTION_BUTTON_KINDS.REJECT === buttonConfig.id) {
      return childEntilements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
      );
    }

    if (ACTION_BUTTON_KINDS.REASSIGN === buttonConfig.id) {
      return childEntilements.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REASSIGNSTEP2
      );
    }
  }

  return false;
};
